/**
 * Role Mapping Constants
 *
 * Maps Business Portal UI roles to Account Service internal roles.
 * Used to maintain consistency across tRPC routers and services.
 *
 * @see Issue #233 - User/Employee Management
 * @see Issue #235 - Tech Debt Resolution
 */

import { UserRole } from '../enums/roles';
import { snakeToTitleCase } from '../utils/string-format.util';

/**
 * Business Portal exposes 4 user-friendly roles:
 * - admin: Full business management access
 * - manager: Team oversight and reporting
 * - member: Standard user access
 * - viewer: Read-only access
 *
 * Account Service uses 3 internal roles:
 * - business_admin: Admin/Manager level access
 * - business_user: Member level access
 * - business_viewer: Viewer (read-only) access
 */
export const BUSINESS_ROLE_TO_ACCOUNT_ROLE = {
  admin: 'business_admin',
  manager: 'business_admin',
  member: 'business_user',
  viewer: 'business_viewer',
} as const;

export type BusinessPortalRole = keyof typeof BUSINESS_ROLE_TO_ACCOUNT_ROLE;
export type AccountServiceRole = (typeof BUSINESS_ROLE_TO_ACCOUNT_ROLE)[BusinessPortalRole];

/**
 * Reverse mapping from Account Service roles to Business Portal display roles.
 * Note: This loses granularity (admin vs manager).
 * Use only for display purposes when detailed role is unknown.
 */
export const ACCOUNT_ROLE_TO_BUSINESS_ROLE: Record<AccountServiceRole, BusinessPortalRole> = {
  business_admin: 'admin',
  business_user: 'member',
  business_viewer: 'viewer',
};

/**
 * Role hierarchy for permission checks.
 * Higher index = more permissions.
 */
export const ROLE_HIERARCHY: readonly BusinessPortalRole[] = ['viewer', 'member', 'manager', 'admin'] as const;

/**
 * Check if roleA has equal or higher permissions than roleB.
 */
export function hasEqualOrHigherRole(roleA: BusinessPortalRole, roleB: BusinessPortalRole): boolean {
  return ROLE_HIERARCHY.indexOf(roleA) >= ROLE_HIERARCHY.indexOf(roleB);
}

/**
 * Get the Account Service role for a Business Portal role.
 */
export function toAccountRole(role: BusinessPortalRole): AccountServiceRole {
  return BUSINESS_ROLE_TO_ACCOUNT_ROLE[role];
}

/**
 * Get the default Business Portal role for an Account Service role.
 */
export function toBusinessRole(role: AccountServiceRole): BusinessPortalRole {
  return ACCOUNT_ROLE_TO_BUSINESS_ROLE[role];
}

/**
 * Human-friendly display labels for Account Service roles (used in emails, notifications).
 * Maps internal enum values like 'business_admin' → 'Business Admin'.
 */
export const ACCOUNT_ROLE_DISPLAY_LABELS: Record<UserRole, string> = {
  [UserRole.BUSINESS_ADMIN]: 'Business Admin',
  [UserRole.BUSINESS_USER]: 'Business User',
  [UserRole.SUPER_ADMIN]: 'Super Admin',
  [UserRole.PLATFORM_ADMIN]: 'Platform Admin',
  [UserRole.BUSINESS_VIEWER]: 'Business Viewer',
  [UserRole.CONSUMER]: 'Consumer',
  [UserRole.LOAN_OFFICER]: 'Loan Officer',
  [UserRole.PROCESSOR]: 'Processor',
  [UserRole.CLOSER]: 'Closer',
  [UserRole.UNDERWRITER]: 'Underwriter',
  [UserRole.SENIOR_UNDERWRITER]: 'Senior Underwriter',
  [UserRole.ANALYST]: 'Analyst',
  [UserRole.OPS_MANAGER]: 'Ops Manager',
  [UserRole.PORTFOLIO_MANAGER]: 'Portfolio Manager',
  [UserRole.FUND_MANAGER]: 'Fund Manager',
  [UserRole.PRINCIPAL]: 'Principal',
  [UserRole.DRAW_INSPECTOR]: 'Draw Inspector',
  [UserRole.SERVICING]: 'Servicing',
};

/**
 * Format an Account Service role enum value for user-facing display.
 * Falls back to title-casing the raw value if not in the map.
 */
export function formatRoleLabel(role: string): string {
  const label = ACCOUNT_ROLE_DISPLAY_LABELS[role as UserRole];
  if (label) {
    return label;
  }
  // Fallback: 'business_admin' → 'Business Admin'
  return snakeToTitleCase(role);
}

/**
 * All available Business Portal roles for dropdowns/selects.
 */
export const BUSINESS_PORTAL_ROLES: readonly BusinessPortalRole[] = ['admin', 'manager', 'member', 'viewer'] as const;
